import { useCallback, useRef, useState } from "react";
import { useJobStore } from "../../stores/jobStore";

export function UploadZone() {
  const { uploadJob } = useJobStore();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const handleFile = useCallback(async (file: File) => {
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setError("Only PDF files are accepted.");
      return;
    }
    setError(null);
    setUploading(true);
    setProgress(0);
    try {
      await uploadJob(file, setProgress);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }, [uploadJob]);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = "";
  };

  return (
    <div className="flex flex-col items-center justify-center h-full gap-8 px-8 animate-fade-in">
      {/* Hero text */}
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-semibold text-white">Digitize a Chart PDF</h2>
        <p className="text-slate-400 text-sm max-w-sm">
          Drop an AFM chart PDF and the pipeline will automatically triage, detect, OCR, and extract all data series.
        </p>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        className={[
          "relative w-full max-w-lg h-56 rounded-2xl border-2 border-dashed transition-all duration-200",
          "flex flex-col items-center justify-center gap-4 cursor-pointer",
          dragging
            ? "border-blue-400 bg-blue-500/10 shadow-glow-blue scale-[1.01]"
            : "border-rdp-border hover:border-blue-500/50 hover:bg-rdp-hover",
          uploading ? "pointer-events-none" : "",
        ].join(" ")}
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-3 w-full px-10">
            <div className="w-10 h-10 rounded-full border-2 border-rdp-border border-t-blue-500 animate-spin" />
            <div className="w-full bg-rdp-border rounded-full h-1.5 overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-slate-400 text-sm font-mono">{progress}% uploading…</p>
          </div>
        ) : (
          <>
            <div className={[
              "w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all",
              dragging ? "bg-blue-500/20 scale-110" : "bg-rdp-border",
            ].join(" ")}>
              📄
            </div>
            <div className="text-center">
              <p className="text-white font-medium">Drop PDF here or <span className="text-blue-400 underline underline-offset-2">browse</span></p>
              <p className="text-slate-500 text-xs mt-1">PDF files only · Max 100 MB</p>
            </div>
          </>
        )}
      </div>

      {error && (
        <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 px-4 py-2 rounded-lg">
          {error}
        </p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="hidden"
        onChange={onInputChange}
      />

      {/* Pipeline stages legend */}
      <div className="flex items-center gap-3 text-xs text-slate-500">
        {["Triage", "Detect", "OCR", "Extract", "Review"].map((s, i) => (
          <span key={s} className="flex items-center gap-1.5">
            {i > 0 && <span className="w-4 h-px bg-rdp-border" />}
            <span className="w-1.5 h-1.5 rounded-full bg-rdp-border" />
            {s}
          </span>
        ))}
      </div>
    </div>
  );
}
